import { useToast } from 'vue-toastification';
import type { ToastID } from 'vue-toastification/dist/types/types';
import StatusToast, { type StatusToastProps, type ToastSeverity } from '~/components/toastify/StatusToast.vue';
import { useAnnouncerStore } from '~/stores/layout/announcer';

/** How long a toast stays up when nothing holds it open. */
export const DEFAULT_TOAST_TIMEOUT_MS = 3000;

export interface StatusToastOptions {
    /** How long the toast stays up, in milliseconds. */
    timeout?: number;
}

export interface NoticeOptions {
    /** What the notice is about. A newer notice about the same subject takes its place. */
    subject?: string;
    onUndo?: () => void;
}

interface RaiseOptions extends StatusToastOptions {
    onClose?: () => void;
}

// The notices on screen, by the id vue-toastification gave each. A notice leaves this in its own onClose, which every
// way a toast closes runs: its timer, a click, its close button, a dismiss and a clear.
const liveNotices = new Map<ToastID, NoticeOptions>();

export const useGlobalToast = () => {
    const toast = useToast();

    /**
     * Every toast is raised here, so this is the one place its lifetime is decided.
     *
     * The timer is this function's, never the library's. vue-toastification closes a timed toast when its progress
     * bar's CSS animation ends, and reset.css collapses every animation to 0.01ms for a reader who asks for reduced
     * motion, so a toast the library timed would close as it opened.
     *
     * A toast holding an action waits for the reader. It never expires, because a few seconds is not enough for
     * someone driving by keyboard or screen reader to reach the control, and a click anywhere on it does not close it,
     * because that click may have been meant for the action. Every other toast expires and closes on a click anywhere.
     * StatusToast is handed the same answer the library is, so its cursor never promises a close the library will not
     * make.
     */
    const raise = (
        props: Omit<StatusToastProps, 'closesOnClick'>,
        { timeout = DEFAULT_TOAST_TIMEOUT_MS, onClose }: RaiseOptions = {}
    ): ToastID => {
        const waitsForReader = props.action !== undefined;
        const closesOnClick = !waitsForReader;
        let expiry: ReturnType<typeof setTimeout> | undefined;

        const id = toast({ component: StatusToast, props: { ...props, closesOnClick } }, {
            timeout: false,
            closeOnClick: closesOnClick,
            // StatusToast draws its own dismiss control, inside the toast's row and its focus handling, and its own
            // severity mark.
            closeButton: false,
            icon: false,
            draggable: false,
            // Neither kind wants the library's role on its wrapper: a status toast puts its alert on its words alone,
            // and a notice is said through the app's announcer.
            accessibility: { toastRole: 'none' },
            onClose: () => {
                clearTimeout(expiry);
                onClose?.();
            }
        });
        if (!waitsForReader) expiry = setTimeout(() => toast.dismiss(id), timeout);
        return id;
    };

    // A status toast is not said through the announcer: a component that needs its outcome heard pairs the toast with
    // a live region of its own, which the announcer would repeat.
    const show = (severity: ToastSeverity, title: string, text: string, options?: StatusToastOptions) => {
        if (!import.meta.client) return;
        raise({ severity, title, text }, options);
    };

    const showSuccess = (title: string, text: string, options?: StatusToastOptions) =>
        show('success', title, text, options);

    const showError = (title: string, text: string, options?: StatusToastOptions) =>
        show('error', title, text, options);

    const showWarning = (title: string, text: string, options?: StatusToastOptions) =>
        show('warning', title, text, options);

    const showInfo = (title: string, text: string, options?: StatusToastOptions) =>
        show('warning', title, text, options);

    // vue-toastification keeps a closing toast in the document through its leave transition, and its Undo with it, so
    // a press counts only while the notice is still live: a second click, or a click on a superseded toast, does
    // nothing.
    const undo = (id: ToastID, onUndo: () => void) => {
        if (!liveNotices.has(id)) return;
        onUndo();
        toast.dismiss(id);
    };

    /**
     * A text-only toast saying what a choice just did, with an Undo when the choice can be reversed.
     *
     * One live Undo at a time. A toast holding Undo waits for the reader (`raise`), so it has to be superseded
     * instead, or a run of choices buries the page under a column of them. Offer an Undo only for a choice that keeps
     * its own route back where it was made.
     *
     * Superseding by subject on top of that is what stops a stale Undo restoring a choice from two decisions ago over
     * the person's newest one.
     *
     * A toast's role arrives with its element, which is not reliably read out, so the notice is said through the
     * app-level live region instead, and the toast takes no role so nothing is read twice. Closing takes the sentence
     * back: it can name a member's data, and the region must not keep it once the notice, or the session it belongs
     * to, is gone.
     */
    const showNotice = (text: string, options: NoticeOptions = {}) => {
        if (!import.meta.client) return;
        const { subject, onUndo } = options;

        const superseded = [...liveNotices].filter(([, live]) =>
            (subject !== undefined && live.subject === subject) || (onUndo !== undefined && live.onUndo !== undefined));
        for (const [id] of superseded) toast.dismiss(id);

        const announcer = useAnnouncerStore();
        const id: ToastID = raise(
            { text, action: onUndo && { label: 'Undo', onPress: () => undo(id, onUndo) } },
            {
                onClose: () => {
                    liveNotices.delete(id);
                    announcer.withdraw(text);
                }
            }
        );
        liveNotices.set(id, options);
        void announcer.announce(text);
    };

    /** Closes every notice, its Undo with it, and leaves status toasts standing. */
    const dismissNotices = () => {
        if (!import.meta.client) return;
        for (const id of [...liveNotices.keys()]) toast.dismiss(id);
    };

    const handleClearToast = () => {
        if (!import.meta.client) return;
        toast.clear();
    };

    return {
        showSuccess,
        showError,
        showWarning,
        showInfo,
        showNotice,
        dismissNotices,
        handleClearToast
    };
};
